/**
 * Resolution shared by the cartesian adapters — `line`, `bar`, `combo`.
 *
 * All three read the same three things out of a §8.6 response before they draw
 * anything: the x column, its category strings, and each series' column
 * definition. Keeping one copy here means a manifest that names a missing
 * column fails the same way with the same message whichever chart it declares,
 * and there is one place to change when the payload shape moves — not three
 * that can drift apart.
 *
 * These functions throw rather than render blank. The manifest validator
 * rejects a missing column at startup, so reaching a throw means the server and
 * the browser disagree about the report; saying so beats an axis-less chart
 * that merely looks empty.
 */

import { columnValues } from '../../lib/charts'
import { asNumber } from '../../lib/format'
import type { Cell, ColumnSpec, VisualSpec } from '../../lib/types'

/** The x column, or a throw naming the visual — never a silent empty axis. */
export function resolveXColumn(
  visual: VisualSpec,
  columns: readonly ColumnSpec[],
): ColumnSpec {
  const xColumn = columns.find((column) => column.key === visual.x)
  if (xColumn === undefined) {
    throw new Error(`visual ${visual.id} has no x column ${String(visual.x)}`)
  }
  return xColumn
}

/**
 * The x column's values as category strings.
 *
 * A null category becomes an empty slot rather than being dropped: removing it
 * would silently close the gap and shift every later point left.
 */
export function categoryData(
  visual: VisualSpec,
  columns: readonly ColumnSpec[],
  rows: readonly (readonly Cell[])[],
): string[] {
  const xColumn = resolveXColumn(visual, columns)
  return columnValues(columns, rows, xColumn.key).map((value) =>
    value === null ? '' : String(value),
  )
}

/**
 * Each series' column definition, in declaration order, with the series' own
 * label overriding the column's. Throws on a column the dataset does not carry.
 */
export function resolveSeriesColumns(
  visual: VisualSpec,
  columns: readonly ColumnSpec[],
): ColumnSpec[] {
  return visual.series.map((series) => {
    const column = columns.find((candidate) => candidate.key === series.column)
    if (column === undefined) {
      throw new Error(`visual ${visual.id} plots unknown column ${series.column}`)
    }
    return { ...column, label: series.label ?? column.label }
  })
}

/**
 * One series' data down the positional rows (§8.6), as numbers.
 *
 * `asNumber` rather than the raw cell: a value the manifest calls a number and
 * the source sent as text plots as a gap, not as a zero.
 */
export function seriesData(
  columns: readonly ColumnSpec[],
  rows: readonly (readonly Cell[])[],
  key: string,
): (number | null)[] {
  return columnValues(columns, rows, key).map(asNumber)
}
